import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Animated custom <Select>. Options: { value, label, description?, badge? }.
 * Controlled via `value` + `onChange(value)`.
 */
export default function Select({
  value,
  onChange,
  options = [],
  label,
  placeholder = 'Select…',
  className,
  buttonClassName,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    const onClick = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  return (
    <div className={cn('relative w-full', className)} ref={ref}>
      {label && (
        <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-content-subtle">
          {label}
        </label>
      )}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          'glass flex h-11 w-full items-center justify-between gap-2 rounded-lg px-3.5 text-left text-sm',
          'transition-colors hover:bg-white/[0.07] focus-visible:ring-2 focus-visible:ring-brand-400',
          'disabled:cursor-not-allowed disabled:opacity-60',
          buttonClassName
        )}
      >
        <span className="flex min-w-0 items-center gap-2">
          {selected?.badge && (
            <span className="rounded bg-brand-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-brand-300">
              {selected.badge}
            </span>
          )}
          <span className={cn('truncate', selected ? 'text-content' : 'text-content-subtle')}>
            {selected ? selected.label : placeholder}
          </span>
        </span>
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 text-content-subtle transition-transform', open && 'rotate-180')}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.ul
            role="listbox"
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="glass-strong absolute z-50 mt-2 max-h-72 w-full overflow-auto rounded-xl p-1.5 shadow-soft no-scrollbar"
          >
            {options.map((opt) => {
              const active = opt.value === value;
              return (
                <li key={opt.value} role="option" aria-selected={active}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(opt.value);
                      setOpen(false);
                    }}
                    className={cn(
                      'flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors',
                      active ? 'bg-brand-500/15' : 'hover:bg-white/[0.06]'
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        {opt.badge && (
                          <span className="rounded bg-white/10 px-1.5 py-0.5 text-[10px] font-semibold text-content-muted">
                            {opt.badge}
                          </span>
                        )}
                        <span className="truncate text-sm font-medium text-content">{opt.label}</span>
                      </div>
                      {opt.description && (
                        <p className="mt-0.5 truncate text-xs text-content-subtle">{opt.description}</p>
                      )}
                    </div>
                    {active && <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-300" />}
                  </button>
                </li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}
